import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  SyntheticEvent,
  useRef,
  KeyboardEvent,
} from "react";
import {
  Button,
  Checkbox,
  Dimmer,
  Divider,
  Dropdown,
  Form,
  FormField,
  FormGroup,
  Icon,
  Input,
  Loader,
  Modal,
  Pagination,
  Popup,
  Tab,
  TabProps,
  TextArea,
} from "semantic-ui-react";
import { useIntl } from "react-intl";
import CardLayout from "../common/CardLayout";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { Table } from "react-lib/frameworks/Table";
import formatComma from "react-lib/utils/formatComma";
import ModConfirmOA from "react-lib/apps/IsHealth/REG/ModConfirmOA";
import moment from "moment";
import CONFIG from "config/config";

// SCSS
import "./CardSaveServiceOrder.scss";

// api
import {
  ACTIONS,
  BTN_ACTS,
  DEFAULT_PRODUCT_LIST_LIMIT,
  MAIN_CARD_NAME,
  PickedState,
  RunSequence,
  SaveServiceOrderType,
  SetProp,
} from "./sequence/SaveServiceOrder";
import DoctorFeeRuleSerializer from "issara-sdk/types/DoctorFeeRuleSerializer_apps_DFC";
import ProfiledDivisionHasUserSerializer from "issara-sdk/types/ProfiledDivisionHasUserSerializer_apps_PRX";
import EmployeeSerializer from "issara-sdk/types/EmployeeSerializer_users";
import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";
import DoctorSerializer from "issara-sdk/types/DoctorSerializer_core";
import ProductServiceSerializer from "issara-sdk/types/ProductServiceSerializer_core";

// UX
import CardSaveServiceOrderUX from "react-lib/apps/MEH/CardSaveServiceOrderUX";
import { SelectedCustomerData, VisitListData } from "./types/MeBillingType";
import SnackMessage from "../common/SnackMessage";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

type CardSaveServiceOrderProps = {
  setProp: SetProp;
  runSequence: RunSequence;
  onEvent: (e: any) => Promise<any>;

  // data
  visitList: VisitListData | undefined;

  // sequence
  SaveServiceOrderSequence?: SaveServiceOrderType;
  // CommonInterface
  searchedItemListWithKey?: Record<string, any>;
  availableSlots?: Record<string, any>[];
  divisionId?: string;
  userTokenize?: Record<string, any>;
  selectedCustomer: SelectedCustomerData | undefined;
  selectedEncounter?: Record<string, any>;
  // options
  doctorList: DoctorSerializer[];
  providerList: EmployeeSerializer[];
  languageUX: "en" | "th";
} & Pick<PickedState, "buttonLoadCheck" | "errorMessage" | "loadingStatus" | "masterOptions">;

const COLOR = {
  grey: `rgba(178, 178, 178, 1)`,
};

const CARD_KEY: string = "CardSaveServiceOrder";

const CardSaveServiceOrder = (props: CardSaveServiceOrderProps) => {
  const intl = useIntl();

  const [openSearch, setOpenSearch] = useState<boolean>(false);
  const [selectService, setSelectService] = useState<DoctorFeeRuleSerializer[]>([]);
  const [searchService, setSearchService] = useState<string>("");
  const [previewDraft, setPreviewDraft] = useState<any[]>([]);
  const [openConfirmDelete, setOpenConfirmDelete] = useState<number | null>();
  const [filterTimeOut, setFilterTimeOut] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    props.runSequence({
      sequence: "SaveServiceOrder",
      restart: true,
    });
  }, []);

  useEffect(() => {
    setPreviewDraft(props.SaveServiceOrderSequence?.serviceOrderPreviewData || []);
  }, [props.SaveServiceOrderSequence?.serviceOrderPreviewData]);

  const encounterListOption = useMemo(() => {
    return (props.SaveServiceOrderSequence?.encounterList || []).map(
      (item: EncounterSerializer) => {
        return {
          key: item.id,
          value: item.id,
          text: item.full_number,
        };
      }
    );
  }, [props.SaveServiceOrderSequence?.encounterList]);

  const doctorList = useMemo(() => {
    return (props.doctorList || []).map((item: DoctorSerializer) => ({
      key: item.employee || 0,
      value: item.employee || 0,
      text:
        (item.full_name || "")?.trim().length > 0
          ? item.full_name
          : `${item.first_name || ""} ${item.last_name || ""}`,
    }));
  }, [props.doctorList]);

  const handlePageChange = useCallback(
    (activePage: number) => {
      handleSearch({ activePage });
    },
    [props.SaveServiceOrderSequence?.serviceListPage, props.runSequence]
  );

  const addUniqueById = (
    list: DoctorFeeRuleSerializer[],
    item: DoctorFeeRuleSerializer
  ): DoctorFeeRuleSerializer[] => {
    if (list.some((i) => i.id === item.id)) return list;
    return [...list, item];
  };

  const handleCheckboxChange = (checked: boolean, item: DoctorFeeRuleSerializer) => {
    setSelectService((prev) => {
      if (checked) {
        return addUniqueById(prev, item);
      } else {
        return prev.filter((i) => i.id !== item.id);
      }
    });
  };

  const isChecked = (id?: number) => selectService.some((i) => i.id === id);

  const serviceList = useMemo(() => {
    return (props.SaveServiceOrderSequence?.serviceList || []).map(
      (item: ProductServiceSerializer) => ({
        ...item,
        code: (
          <div style={{ marginLeft: "10px" }}>
            <Checkbox
              checked={isChecked(item.id)}
              label={item.code}
              onChange={(_, data) => handleCheckboxChange(Boolean(data?.checked), item)}
            />
          </div>
        ),
        name: <div style={{ marginLeft: "15px" }}>{item.name}</div>,
        price: (
          <div style={{ display: "flex", justifyContent: "right", marginRight: "10px" }}>
            {formatComma(item.price || "")}
          </div>
        ),
      })
    );
  }, [props.SaveServiceOrderSequence?.serviceList, selectService]);

  // แก้ค่า draft
  const handleChangeQuantity = (index: number, value: string) => {
    setPreviewDraft((prev) => {
      const data = [...prev];
      data[index] = {
        ...data[index],
        quantity: value.replace(/\D/g, ""),
      };

      return data;
    });
  };

  // update prop
  const commitQuantity = (index: number) => {
    const draftValue = previewDraft[index]?.quantity ?? "";

    const quantity = Number(draftValue) > 0 ? Number(draftValue) : 1;

    const newData = [...(props.SaveServiceOrderSequence?.serviceOrderPreviewData ?? [])];

    newData[index] = {
      ...newData[index],
      quantity: quantity,
      price_total: quantity * Number(newData[index]?.price_unit ?? 0),
    };

    props.setProp("SaveServiceOrderSequence.serviceOrderPreviewData", newData);

    // อัพเดต draft ให้ตรงกับ prop จริง
    setPreviewDraft((prev) => {
      const nextDraft = [...prev];
      nextDraft[index] = {
        ...nextDraft[index],
        quantity: quantity.toString(),
      };
      return nextDraft;
    });
  };

  const handleKeyDown = (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      commitQuantity(index);
      e.currentTarget.blur();
    }
  };

  const handleDeleteServiceOrderPreview = () => {
    props.runSequence({
      sequence: "SaveServiceOrder",
      action: ACTIONS.DELETE_SERVICE_ORDER,
      id: openConfirmDelete || 0,
      callback: (open: number | null) => {
        setOpenConfirmDelete(open);
      },
    });
  };

  const serviceOrderPreviewData = useMemo(() => {
    return (previewDraft || []).map((item: any, index: number) => ({
      ...item,
      quantity: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Input
            className="yellow-rounded"
            value={item.quantity}
            inputMode="numeric"
            onChange={(_, { value }) => handleChangeQuantity(index, value)}
            onBlur={() => commitQuantity(index)}
            onKeyDown={handleKeyDown(index)}
          />
        </div>
      ),
      price_unit: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {formatComma(item.price_unit || 0)}
        </div>
      ),
      price_total: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {formatComma(item.price_total || 0)}
        </div>
      ),
      perform_datetime: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {item.perform_datetime ? moment(item.perform_datetime).format("DD/MM/YYYY") : "-"}
        </div>
      ),
      delete: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            ...(item.is_paid && { display: "none" }),
          }}
        >
          <Button
            size="mini"
            icon="minus"
            style={{
              background: "rgba(255, 231, 231, 1)",
              color: "rgba(255, 0, 0, 1)",
              borderRadius: "16px",
              padding: "4px 10px",
            }}
            onClick={() => setOpenConfirmDelete(index)}
          />
        </div>
      ),
      no: <div style={{ display: "flex", justifyContent: "center" }}>{index + 1}</div>,
      service_code: (
        <div style={{ display: "flex", justifyContent: "center" }}>{item.service_code}</div>
      ),
    }));
  }, [previewDraft, props.SaveServiceOrderSequence?.serviceOrderPreviewData]);

  const serviceDoctorPreviewData = useMemo(() => {
    return (props.SaveServiceOrderSequence?.serviceDoctorPreviewData || []).map((item: any) => {
      return {
        ...item,
      };
    });
  }, [props.SaveServiceOrderSequence?.serviceDoctorPreviewData]);

  const sumTotal = useMemo(() => {
    const sumServiceOrder = (props.SaveServiceOrderSequence?.serviceOrderPreviewData ?? []).reduce(
      (acc, item) => acc + Number(item.price_total || 0),
      0
    );

    const sumServiceFeeItem = (
      props.SaveServiceOrderSequence?.serviceDoctorPreviewData || []
    ).reduce((acc, item) => acc + Number(item.compensation_price || 0), 0);

    return formatComma(sumServiceOrder + sumServiceFeeItem);
  }, [
    props.SaveServiceOrderSequence?.serviceOrderPreviewData,
    props.SaveServiceOrderSequence?.serviceDoctorPreviewData,
  ]);

  const showSearchResult = useMemo(() => {
    return !openSearch &&
      props.SaveServiceOrderSequence?.serviceList &&
      props.SaveServiceOrderSequence?.serviceList?.length > 0 &&
      searchService.length > 3
      ? true
      : false;
  }, [props.SaveServiceOrderSequence?.serviceList, openSearch]);

  const handleOpenModalServiceOrder = () => {
    handleSearch();

    setSelectService([]);
    setSearchService("");
    setOpenSearch(true);
  };

  const handleSearch = (params: any = null) => {
    props.runSequence({
      sequence: "SaveServiceOrder",
      action: ACTIONS.SEARCH,
      searchText: searchService,
      ...(params ? { ...params } : {}),
    });
  };

  const handleAddServiceOrder = () => {
    props.runSequence({
      sequence: "SaveServiceOrder",
      action: ACTIONS.ADD_SERVICE_ORDER,
      selectService: selectService,
      callback: (open: boolean) => {
        setOpenSearch(open);
        setSearchService("");
      },
    });
  };

  const handleSave = () => {
    if (serviceDoctorPreviewData.some((item) => !item.id &&
      !props.doctorList.some((doctor) => Number(doctor.employee) === Number(item.received_employee)))) {
      props.setProp(`errorMessage.${MAIN_CARD_NAME}`, "กรุณาเลือกแพทย์ผู้รับค่าธรรมเนียมที่เป็นผู้ให้บริการของ OA นี้");
      return;
    }
    props.runSequence({
      sequence: "SaveServiceOrder",
      action: ACTIONS.SAVE_SERVICE_ORDER,
    });
  };

  const handleCloseModConfirm = () => setOpenConfirmDelete(null);

  const handleSearchText = (e: any, v: any) => {
    const text = v.value;

    let timeOut;

    if (filterTimeOut !== null) {
      clearTimeout(filterTimeOut);
    }

    setSearchService(text);

    if (props.selectedEncounter?.id) {
      timeOut = setTimeout(() => {
        if (searchService.length >= 3) {
          props.runSequence({
            sequence: "SaveServiceOrder",
            action: ACTIONS.SEARCH,
            searchText: searchService,
          });
        } else if (searchService.length === 1) {
          props.setProp(`SaveServiceOrderSequence.serviceList`, []);
        }
      }, 800);
      setFilterTimeOut(timeOut as any);
    }
  };

  const handleAddServiceDoctorPreview = (item: any) => {
    const newData = [
      ...(props.SaveServiceOrderSequence?.serviceDoctorPreviewData ?? []),
      {
        tempId: crypto.randomUUID(),
        encounter: props.selectedEncounter?.id,
        doctor_fee_rule: 305352,
        doctor_fee_rule_display: "ค่าธรรมเนียมผู้ให้บริการ",
        service_code: "18010015",
        quantity: 1,
        price_unit: 0,
        compensation_price: 0,
      },
    ];

    return props.setProp(`SaveServiceOrderSequence.serviceDoctorPreviewData`, newData);
  };

  const handleDeleteServiceDoctorPreview = (item: any) => {
    props.runSequence({
      sequence: "SaveServiceOrder",
      action: ACTIONS.DELETE_SERVICE_DOCTOR,
      data: item,
    });
  };

  const handleChangeServiceDoctorPreview = (item: any, field: string, value: number) => {
    const list = props.SaveServiceOrderSequence?.serviceDoctorPreviewData || [];

    const selectedDoctor =
      field === "received_employee" ? props.doctorList.find((d) => d.employee === value) : null;

    const newData = list.map((data) =>
      (item?.tempId ? data?.tempId !== item.tempId : data?.id !== item?.id)
        ? data
        : field === "received_employee"
        ? {
            ...data,
            received_employee: selectedDoctor?.employee || "",
            received_employee_display: selectedDoctor?.name_code || "",
          }
        : {
            ...data,
            [field]: value,
          }
    );

    props.setProp("SaveServiceOrderSequence.serviceDoctorPreviewData", newData);
  };

  const handleChangeEncounter = (e: any, data: any) => {
    const encounter = (props.SaveServiceOrderSequence?.encounterList || []).find(
      (item: EncounterSerializer) => item.id === data.value
    );

    props.runSequence({
      sequence: "SaveServiceOrder",
      action: ACTIONS.SELECTED_ENCOUNTER,
      encounterData: encounter,
    });
  };

  return (
    <div className="card-save-service-order">
      <SnackMessage
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${MAIN_CARD_NAME}`, null);
        }}
        error={props.errorMessage?.[MAIN_CARD_NAME]}
        success={null}
        languageUX={props.languageUX}
      />

      <Dimmer active={props.loadingStatus?.[MAIN_CARD_NAME]} inverted>
        <Loader inverted>Loading</Loader>
      </Dimmer>

      <CardSaveServiceOrderUX
        selectedEncounter={props.selectedEncounter}
        encounterListOption={encounterListOption}
        onChangeEncounter={handleChangeEncounter}
        sumTotal={sumTotal}
        serviceOrderPreviewData={serviceOrderPreviewData}
        showSearchResult={showSearchResult}
        onChangeSearchText={handleSearchText}
        searchText={searchService}
        searchResult={
          showSearchResult &&
          (props.SaveServiceOrderSequence?.serviceList || []).map((item: any, index: number) => (
            <div
              key={index}
              style={{
                cursor: "pointer",
                padding: "5px",
                borderBottom: "1px solid #EEEEEE",
              }}
              onClick={(e: any) => {
                props.runSequence({
                  sequence: "SaveServiceOrder",
                  action: ACTIONS.ADD_SERVICE_ORDER,
                  selectService: [item],
                  callback: (open: boolean) => {
                    if (!open) {
                      props.setProp(`SaveServiceOrderSequence.serviceList`, []);
                      setSearchService("");
                    }
                  },
                });
              }}
            >
              <div>{item.name}</div>
              <div style={{ color: "rgba(174,174,174)" }}>{item.code}</div>
            </div>
          ))
        }
        serviceFeeItem={serviceDoctorPreviewData.map((item: any, index: number) => {
          return (
            <Form>
              <FormGroup style={{ alignItems: "center", display: "flex" }}>
                <FormField
                  style={{ margin: "0px 20px 0px 55px", minWidth: "max-content", display: "flex" }}
                >
                  <label>ค่าธรรมเนียมผู้ให้บริการ</label>
                  <span style={{ color: "red" }}>*</span>
                </FormField>
                <FormField width={8}>
                  <Dropdown
                    fluid
                    search
                    selection
                    clearable
                    icon="search"
                    placeholder="ค้นหาชื่อผู้ให้บริการ"
                    options={item.id && item.received_employee && !doctorList.some((doctor) => Number(doctor.value) === Number(item.received_employee))
                      ? [...doctorList, { key: item.received_employee, value: item.received_employee, text: item.received_employee_display || "ผู้รับค่าธรรมเนียมเดิม", disabled: true }]
                      : doctorList}
                    value={item.received_employee || ""}
                    onChange={(e: any, data: any) =>
                      handleChangeServiceDoctorPreview(item, "received_employee", data.value)
                    }
                  />
                </FormField>
                <FormField style={{ margin: "0px 20px", minWidth: "max-content" }}>
                  <label>จำนวน (บาท)</label>
                </FormField>
                <FormField width={3}>
                  <Input
                    fluid
                    value={item.compensation_price}
                    onChange={(e: any, data: any) => {
                      handleChangeServiceDoctorPreview(item, "compensation_price", data.value);
                    }}
                  ></Input>
                </FormField>
                <FormField style={{ marginRight: "20px" }}>
                  <FormField style={{ marginRight: "20px" }}>
                    <Button
                      className="circular"
                      size="mini"
                      icon={index === 0 ? "plus" : "minus"}
                      style={
                        index === 0
                          ? { background: "var(--primary-theme-color)", color: "white" }
                          : { background: "rgba(255, 231, 231, 1)", color: "rgba(255, 0, 0, 1)" }
                      }
                      onClick={() =>
                        index === 0
                          ? handleAddServiceDoctorPreview(item)
                          : handleDeleteServiceDoctorPreview(item)
                      }
                    />
                  </FormField>
                </FormField>
              </FormGroup>
            </Form>
          );
        })}
        buttonOpenURL={
          
          <Button
            style={{ background: "var(--primary-theme-color)", color: "white" }}
            size="small"
            disabled={!props.selectedEncounter?.id}
            icon
            labelPosition="left"
            onClick={() => {
              window.open(`https://mehey-staging.ishealth.app/liff-doctor-lobby-chat/${props.selectedEncounter?.id}`)
            }}
          >
            <Icon name="doctor" />
             Telemed Now
          </Button>
            
          
        }
        buttonCopy={
          <Popup
            content="คัดลอกลิงค์แล้ว!"
            open={copiedLink}
            position="top center"
            trigger={
              <Button
                style={copiedLink
                  ? { background: "#21ba45", color: "white", transition: "background 0.3s" }
                  : { background: "var(--primary-theme-color)", color: "white" }}
                size="small"
                disabled={!props.selectedEncounter?.id}
                icon
                labelPosition="left"
                onClick={() => {
                  if (props.selectedEncounter?.id && props.selectedEncounter?.doctor && props.selectedEncounter?.division) {
                    navigator.clipboard.writeText(`https://liff.line.me/${CONFIG.MOBILE_LINE_LIFF_ID}/?goto=doctor&encounter=${props.selectedEncounter?.id}&division=${props.selectedEncounter?.division}`);
                    setCopiedLink(true);
                    setTimeout(() => setCopiedLink(false), 2000);
                  }
                }}
              >
                <Icon name={copiedLink ? "check" : "copy outline"} />
                {copiedLink ? "คัดลอกแล้ว!" : "ลิงค์สำหรับผู้ให้บริการ"}
              </Button>
            }
          />
        }
        buttonSearch={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleOpenModalServiceOrder}
            disabled={!props.selectedEncounter?.id}
            // config
            style={{ background: "var(--primary-theme-color)", color: "white" }}
            fluid={true}
            size="small"
            title={"ค้นหาข้อมูล"}
          />
        }
        buttonSave={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleSave}
            // data
            paramKey={BTN_ACTS.SAVE_SERVICE_ORDER}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SAVE_SERVICE_ORDER]}
            // config
            style={{ background: "var(--primary-theme-color)", color: "white" }}
            fluid={true}
            size="small"
            title={"บันทึกข้อมูล"}
          />
        }
      />

      <Modal onClose={() => setOpenSearch(false)} open={openSearch} size="large">
        <CardLayout
          titleText={"ค้นหารายการบริการ"}
          closeable={false}
          toggleable={false}
          hideHeaderIcon={true}
        >
          <Form>
            <Form.Group inline>
              <Form.Field width={2} style={{ minWidth: "max-content" }}>
                <label>{"ชื่อหรือรหัส"}</label>
              </Form.Field>
              <Form.Field width={12}>
                <Input
                  value={searchService}
                  onChange={(event: any, { value }: any) => {
                    setSearchService(value);
                  }}
                />
              </Form.Field>
              <Form.Field width={2}>
                <ButtonLoadCheck
                  // function
                  setProp={props.setProp}
                  onClick={handleSearch}
                  // data
                  paramKey={BTN_ACTS.SEARCH}
                  buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
                  // config
                  style={{ background: "var(--primary-theme-color)", color: "white" }}
                  fluid={true}
                  size="small"
                  title={"ค้นหา"}
                />
              </Form.Field>
            </Form.Group>
          </Form>
          <Divider />
          <Table
            defaultPageSize="10"
            data={serviceList}
            headers="รหัสบริการ,ชื่อบริการ,ราคาต่อหน่วย"
            keys="code,name,price"
            widths="150,auto,200"
            minRows="10"
            pageSize="10"
            showPagination={false}
            style={{ height: "440px" }}
          ></Table>
          <Divider />
          <Form>
            <Form.Group inline style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ display: "flex" }}>
                <PaginationShorthand
                  activePage={props.SaveServiceOrderSequence?.serviceListPage?.activePage || 1}
                  total={props.SaveServiceOrderSequence?.serviceListPage?.total || 0}
                  limit={DEFAULT_PRODUCT_LIST_LIMIT}
                  onPageChange={handlePageChange}
                />
              </div>
              <div style={{ display: "flex" }}>
                <div style={{ marginRight: "10px" }}>
                  <ButtonLoadCheck
                    // function
                    setProp={props.setProp}
                    onClick={handleAddServiceOrder}
                    // data
                    paramKey={BTN_ACTS.ADD_SERVICE_ORDER}
                    buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.ADD_SERVICE_ORDER]}
                    // config
                    style={{ background: "var(--primary-theme-color)", color: "white" }}
                    fluid={true}
                    size="small"
                    title={"ยืนยันเลือกรายการ"}
                  />
                </div>
                <div>
                  <Button
                    type="button"
                    fluid
                    size="small"
                    style={{ background: COLOR.grey, color: "white" }}
                    content={intl.formatMessage({ id: "ยกเลิก" })}
                    onClick={() => {
                      setOpenSearch(false);
                      setSearchService("");
                    }}
                  />
                </div>
              </div>
            </Form.Group>
          </Form>
        </CardLayout>
      </Modal>

      <ModConfirmOA
        approveButtonPosition="right"
        approveButtonText="ตกลง"
        denyButtonText="ยกเลิก"
        open={typeof openConfirmDelete === "number"}
        title="ต้องการลบรายการค่าธรรมเนียมหรือไม่"
        onApprove={handleDeleteServiceOrderPreview}
        onClose={handleCloseModConfirm}
        onDeny={handleCloseModConfirm}
        subtitle={""}
      />
    </div>
  );
};

CardSaveServiceOrder.displayName = "CardSaveServiceOrder";
export default CardSaveServiceOrder;
